import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Award,
  Users,
  Target,
  Zap,
  CheckCircle,
  ArrowRight,
  Dumbbell
} from 'lucide-react';

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/t5b9yggm_hyrox3b.jpg";
const HYROX_LOGO = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/afxwgvuq_hyrox-official-gym.png";
const SLED_PUSH_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/uzj5tkje_sled-push-sm.jpg";
const HYROX_RUNNERS_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/n8w6809s_hyrox-runners.jpg";

const raceFormatColumn1 = [
  '1km Run', 'Ski Erg', '1km Run', 'Sled Push', '1km Run', 'Sled Pull', '1km Run', 'Burpee Broad Jumps'
];

const raceFormatColumn2 = [
  '1km Run', 'Rowing', '1km Run', 'Farmers Carry', '1km Run', 'Lunges', '1km Run', 'Wall Balls'
];

const whyXTeam = [
  { icon: Award, title: 'Official HYROX Affiliate', desc: 'Train with a gym that is connected to the sport and understands what it demands.' },
  { icon: Target, title: 'Coach-Led Programming', desc: 'Build the endurance, strength, and pacing strategy HYROX requires.' },
  { icon: Users, title: 'Team Energy', desc: 'Training is more effective when you have coaches and teammates pushing with you.' },
  { icon: Zap, title: 'A Strong Fitness Base', desc: 'Our broader training approach helps you improve not just race readiness, but overall strength and resilience.' },
];

const whoItsFor = [
  'fitness enthusiasts who want a new challenge',
  'runners who want more strength and structure',
  'gym-goers who want a competitive goal',
  'adults who love performance-based training',
  'anyone who wants hybrid fitness with purpose'
];

const whatToExpect = [
  'running intervals and aerobic work',
  'sled pushes and pulls',
  'rowing and ski erg work',
  'wall balls, lunges, carries, and burpees',
  'race pacing strategy',
  'strength and conditioning support'
];

export default function Hyrox() {
  return (
    <div data-testid="hyrox-page">
      {/* Hero Section - Full Width Image */}
      <section 
        className="relative flex items-center justify-center"
        style={{ height: '735px', marginTop: '0' }}
        data-testid="hyrox-hero"
        aria-label="HYROX hero"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HERO_IMAGE})`,
            top: '135px',
            height: '600px'
          }}
        >
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center" style={{ marginTop: '135px' }}>
          <div className="max-w-3xl mx-auto">
            {/* Text overlay box with semi-transparent background */}
            <div className="bg-black/30 p-[50px] inline-block">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-none text-sm font-semibold mb-6">
                <Award className="w-4 h-4" />
                Official HYROX Affiliate
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6">
                HYROX Training in Richmond, VA
              </h1>
              
              <p className="text-lg text-white/90 mb-8">
                Train with an official HYROX affiliate and prepare for race day with expert coaching, structured programming, and a team that pushes you to perform at your best.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button 
                    data-testid="hyrox-hero-cta-primary"
                    className="w-full sm:w-auto bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    Book a Free Intro
                  </Button>
                </a>
                <Link to="/schedule">
                  <Button 
                    variant="outline"
                    data-testid="hyrox-hero-cta-secondary"
                    className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-gray-900 rounded-none font-nav uppercase tracking-widest px-8 py-4 transition-all duration-300"
                  >
                    View HYROX Schedule
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is HYROX */}
      <section className="section-padding bg-white" data-testid="what-is-hyrox" aria-label="What is HYROX">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Official HYROX Gym Logo */}
              <img 
                src={HYROX_LOGO}
                alt="Official HYROX Gym"
                className="h-24 w-auto mb-8"
                loading="lazy"
              />
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6">
                What is HYROX?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                HYROX is a fitness race that combines running with functional workout stations in a format designed to test endurance, strength, pacing, and grit.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                It is competitive, measurable, and addictive in the best way.
              </p>
              <p className="text-gray-700 font-semibold">
                Whether you are new to HYROX or already planning your next race, we can help you train smarter and show up ready.
              </p>
            </div>
            <div className="relative">
              <img 
                src={SLED_PUSH_IMAGE}
                alt="HYROX Sled Push Training"
                className="rounded-none shadow-xl w-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Train at X-Team */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="why-xteam-hyrox" aria-label="Why train at X-Team">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              Why Train for HYROX at X-Team?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyXTeam.map((item, i) => (
              <div 
                key={i} 
                className="bg-white rounded-none p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-2"
                data-testid={`why-xteam-card-${i}`}
              >
                <div className="w-14 h-14 rounded-none bg-gray-100 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="section-padding bg-white" data-testid="who-its-for-hyrox" aria-label="Who HYROX is for">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={HYROX_RUNNERS_IMAGE}
                alt="HYROX Athletes Running"
                className="rounded-none shadow-xl w-full"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6">
                Who HYROX Training is For
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                HYROX training is great for:
              </p>
              <ul className="space-y-4 mb-8">
                {whoItsFor.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF5A3C] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-700 font-semibold">
                You do not have to be elite to start. You just need the desire to train with purpose.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HYROX Race Format */}
      <section className="section-padding bg-[#1A1A1A]" data-testid="hyrox-race-format" aria-label="HYROX race format">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-white mb-4">
              HYROX Race Format
            </h2>
            <p className="text-lg text-white/80">
              8 rounds of 1km runs alternating with functional workout stations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Column 1 */}
            <div className="space-y-4">
              {raceFormatColumn1.map((item, i) => (
                <div key={i} className={`flex items-center gap-3 ${i % 2 === 0 ? 'text-[#FF5A3C]' : 'text-white'}`}>
                  <div className={`w-10 h-10 rounded-none flex items-center justify-center text-sm font-bold ${i % 2 === 0 ? 'bg-gray-800 text-[#FF5A3C]' : 'bg-gray-700 text-white'}`}>
                    {i + 1}
                  </div>
                  <span className="font-medium text-lg">{item}</span>
                </div>
              ))}
            </div>
            
            {/* Column 2 */}
            <div className="space-y-4">
              {raceFormatColumn2.map((item, i) => (
                <div key={i} className={`flex items-center gap-3 ${i % 2 === 0 ? 'text-[#FF5A3C]' : 'text-white'}`}>
                  <div className={`w-10 h-10 rounded-none flex items-center justify-center text-sm font-bold ${i % 2 === 0 ? 'bg-gray-800 text-[#FF5A3C]' : 'bg-gray-700 text-white'}`}>
                    {i + 9}
                  </div>
                  <span className="font-medium text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="what-to-expect-hyrox" aria-label="What to expect">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
                What HYROX Training Looks Like
              </h2>
              <p className="text-lg text-gray-600">
                Your HYROX training may include:
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {whatToExpect.map((item, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-3 bg-white rounded-none p-4 shadow-sm"
                >
                  <Dumbbell className="w-5 h-5 text-[#FF5A3C]" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            
            <p className="text-center text-gray-600 mt-8">
              Built to help you perform on race day and become a stronger all-around athlete.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#FF5A3C] to-[#E64526] text-white" data-testid="hyrox-final-cta" aria-label="Call to action">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading mb-6">
            Ready to Train for HYROX?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book a Free Intro and talk with a coach about your current fitness level, race goals, and the best path to get started.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="hyrox-final-cta-btn"
              className="bg-white text-[#FF5A3C] hover:bg-gray-100 rounded-none font-nav uppercase tracking-widest px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              Book a Free Intro <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
